import React, { useState, useEffect } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Users, UserCheck, Mail, Phone, Shield } from "lucide-react";
import { Link } from "react-router-dom";

export default function SupportStaff() {
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", support_role_id: "", active: true });

  const load = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([api.get("/support-users"), api.get("/support-roles")]);
      setItems(u.data); setRoles(r.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    if (roles.length === 0) { toast.error("أنشئ دوراً واحداً على الأقل أولاً من صفحة أدوار الدعم"); return; }
    setEditing(null);
    setForm({ name: "", email: "", password: "", phone: "", support_role_id: roles[0]?.id || "", active: true });
    setDlg(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: "", phone: u.phone || "", support_role_id: u.support_role_id, active: u.active !== false });
    setDlg(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("الاسم مطلوب");
    if (!editing && !form.email.trim()) return toast.error("الإيميل مطلوب");
    if (!editing && !form.password) return toast.error("كلمة المرور مطلوبة");
    if (!form.support_role_id) return toast.error("اختر الدور");
    try {
      if (editing) {
        const body = { name: form.name, phone: form.phone, support_role_id: form.support_role_id, active: form.active };
        if (form.password) body.password = form.password;
        await api.put(`/support-users/${editing.id}`, body);
      } else {
        await api.post("/support-users", { name: form.name, email: form.email, password: form.password, phone: form.phone, support_role_id: form.support_role_id });
      }
      toast.success(editing ? "تم التحديث" : "تم إنشاء الموظف");
      setDlg(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (u) => {
    if (!window.confirm(`حذف الموظف "${u.name}"؟ سيتم إلغاء جميع تصاريح الدخول النشطة.`)) return;
    try { await api.delete(`/support-users/${u.id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="support-staff-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><Users className="w-6 h-6 text-primary" /> موظفو الدعم</h1>
          <p className="text-sm text-muted-foreground mt-1">فريق شركتك للدعم الفني والمحاسبة الذين يخدمون العملاء المشتركين</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild><Link to="/support/roles"><Shield className="w-4 h-4 me-1" /> إدارة الأدوار</Link></Button>
          <Button onClick={openNew} className="gap-2" data-testid="new-support-user-btn"><Plus className="w-4 h-4" /> موظف دعم جديد</Button>
        </div>
      </div>

      {loading ? <div className="text-center text-muted-foreground py-12">جاري التحميل...</div> :
       items.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed rounded-lg text-muted-foreground">
          <Users className="w-16 h-16 mx-auto opacity-30 mb-3" />
          <div>لا يوجد موظفو دعم بعد</div>
          <Button variant="link" onClick={openNew} className="mt-2">أضف الموظف الأول</Button>
        </div>
       ) : (
        <div className="border rounded-lg overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="text-start p-3">الاسم</th>
                <th className="text-start p-3">الإيميل</th>
                <th className="text-start p-3">الدور</th>
                <th className="text-center p-3">الحالة</th>
                <th className="text-center p-3">تصاريح نشطة</th>
                <th className="text-center p-3">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {items.map(u => (
                <tr key={u.id} className="border-b hover:bg-muted/30" data-testid={`support-user-${u.id}`}>
                  <td className="p-3 font-medium">{u.name}</td>
                  <td className="p-3 text-muted-foreground"><Mail className="w-3 h-3 inline me-1" /> {u.email}</td>
                  <td className="p-3"><span className="px-2 py-0.5 rounded bg-primary/10 text-primary text-xs">{u.support_role_name}</span></td>
                  <td className="p-3 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs ${u.active !== false ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {u.active !== false ? "نشط" : "معطّل"}
                    </span>
                  </td>
                  <td className="p-3 text-center font-mono">
                    <span className={`px-2 py-0.5 rounded text-xs ${u.active_grants > 0 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                      {u.active_grants || 0}
                    </span>
                  </td>
                  <td className="p-3 text-center">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(u)}><Edit2 className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => del(u)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
       )}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent data-testid="support-user-dialog">
          <DialogHeader><DialogTitle>{editing ? "تعديل موظف الدعم" : "موظف دعم جديد"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>الاسم</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" /></div>
              <div><Label>الهاتف</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            </div>
            <div><Label>الإيميل</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} data-testid="staff-email" /></div>
            <div>
              <Label>كلمة المرور {editing && <span className="text-xs text-muted-foreground">(اتركه فارغاً للإبقاء)</span>}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password" />
            </div>
            <div>
              <Label>الدور</Label>
              <Select value={form.support_role_id} onValueChange={(v) => setForm({ ...form, support_role_id: v })}>
                <SelectTrigger data-testid="staff-role"><SelectValue placeholder="اختر دور" /></SelectTrigger>
                <SelectContent>{roles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {editing && (
              <div className="flex items-center justify-between p-3 rounded-md border bg-muted/30">
                <Label>الحساب نشط</Label>
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>إلغاء</Button>
            <Button onClick={save} data-testid="save-staff-btn">حفظ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
